import { getNextBasePath, normalizePossessionStatistics, PUBLIC_MATCH_OPERATIONS_V2_MEDIA_TYPE } from '@y-sports/api-client';
import type { MatchSummary } from '@y-sports/api-client/generated-next-management/src/models';
import type { CenterSnapshot } from './publicMatchCenter';

export type PublicTeamPlayer = {
  id: string;
  displayName: string;
  playerNumber?: number;
  matchesPlayed: number;
  goals: number;
  attempts: number;
  saves: number;
  shotsFaced: number;
  posts: number;
  blockedShots: number;
  technicalFaults: number;
  goalPercentage: number | null;
  savePercentage: number | null;
  lostBalls: number;
  turnovers: number;
};

export type PublicTeamRoster = { players: PublicTeamPlayer[]; unavailableMatches: number };

// The public endpoint supplies full given names and family-name initials.
// Preserve its label: a flat string cannot tell us where family names start.
export function abbreviatePublicPlayerName(value?: string | null): string {
  return value?.trim().replace(/\s+/g, ' ') || '';
}

async function loadSnapshot(id: string, signal: AbortSignal): Promise<CenterSnapshot> {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal.addEventListener('abort', cancel, { once: true });
  if (signal.aborted) cancel();
  const timeout = setTimeout(cancel, 10000);
  try {
    const response = await fetch(`${getNextBasePath()}/public/matches/${encodeURIComponent(id)}/operations/snapshot`, {
      signal: controller.signal,
      headers: { Accept: PUBLIC_MATCH_OPERATIONS_V2_MEDIA_TYPE },
    });
    if (!response.ok) throw new Error(`Public roster unavailable (${response.status})`);
    return await response.json();
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', cancel);
  }
}

const playedStatuses = new Set(['LIVE', 'IN_PROGRESS', 'INPROGRESS', 'RUNNING', 'PAUSED', 'PERIOD_COMPLETE', 'FINISHED', 'COMPLETED', 'ENDED']);
const statisticFields = ['goals', 'attempts', 'saves', 'shotsFaced', 'posts', 'blockedShots', 'technicalFaults', 'lostBalls', 'turnovers'] as const;

/** Aggregate only published match rosters; never fetch private player profiles. */
export async function loadTeamRoster(matches: MatchSummary[], teamId: string, signal: AbortSignal): Promise<PublicTeamRoster> {
  signal.throwIfAborted();
  const uniqueMatches = [...new Map(matches.filter(match => match.id && (match.teamA?.id === teamId || match.teamB?.id === teamId))
    .map(match => [match.id!, match])).values()];
  const snapshots = new Map<string, CenterSnapshot>();
  let unavailableMatches = 0;
  let cursor = 0;
  await Promise.all(Array.from({ length: Math.min(4, uniqueMatches.length) }, async () => {
    while (cursor < uniqueMatches.length) {
      signal.throwIfAborted();
      const match = uniqueMatches[cursor++];
      try {
        snapshots.set(match.id!, await loadSnapshot(match.id!, signal));
      } catch {
        signal.throwIfAborted();
        unavailableMatches++;
      }
    }
  }));
  signal.throwIfAborted();
  const players = new Map<string, PublicTeamPlayer>();
  for (const match of uniqueMatches) {
    const snapshot = snapshots.get(match.id!);
    if (!snapshot) continue;
    const side = snapshot.participants.teamA.id === teamId ? 'TEAM_A'
      : snapshot.participants.teamB.id === teamId ? 'TEAM_B' : undefined;
    if (!side) { unavailableMatches++; continue; }
    const roster = side === 'TEAM_A' ? snapshot.participants.teamA : snapshot.participants.teamB;
    const started = playedStatuses.has(snapshot.state.status.toUpperCase()) || playedStatuses.has((match.status || '').toUpperCase());
    const statistics = new Map((snapshot.state.participantStatistics || []).filter(row => row.teamSide === side)
      .map(row => [row.participantId, normalizePossessionStatistics(row)]));
    const seen = new Set<string>();
    for (const participant of roster.players) {
      if (seen.has(participant.id)) continue;
      seen.add(participant.id);
      const player: PublicTeamPlayer = players.get(participant.id) || {
        id: participant.id, displayName: '', matchesPlayed: 0, goals: 0, attempts: 0,
        saves: 0, shotsFaced: 0, posts: 0, blockedShots: 0, technicalFaults: 0, goalPercentage: null, savePercentage: null, lostBalls: 0, turnovers: 0,
      };
      player.displayName = abbreviatePublicPlayerName(participant.displayName) || player.displayName;
      if (participant.playerNumber != null) player.playerNumber = participant.playerNumber;
      if (started) player.matchesPlayed++;
      const stats = statistics.get(participant.id);
      if (stats) {
        for (const field of statisticFields) player[field] += stats[field] ?? 0;
      }
      players.set(player.id, player);
    }
  }
  return {
    players: [...players.values()].map(player => ({
      ...player,
      goalPercentage: player.attempts > 0 ? Math.round(player.goals / player.attempts * 100) : null,
      savePercentage: player.shotsFaced > 0 ? Math.round(player.saves / player.shotsFaced * 100) : null,
    })).sort((a, b) => (a.playerNumber ?? Infinity) - (b.playerNumber ?? Infinity) || a.displayName.localeCompare(b.displayName)),
    unavailableMatches,
  };
}
